import * as React from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Check, ArrowRight, Monitor, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

type Plan = {
  id: string;
  name: string;
  monthly: string;
  yearly: string;
  tagline: string;
  features: string[];
  cta: string;
  highlight?: boolean;
  badge?: string;
  isFree?: boolean;
  isEnterprise?: boolean;
};

const PLANS: Plan[] = [
  {
    id: "free",
    name: "Miễn phí",
    monthly: "0đ",
    yearly: "0đ",
    tagline: "Dùng thử, không giới hạn thời gian",
    features: ["1 lớp", "5 học viên", "Điểm danh cơ bản", "Giao bài tập", "Admin = Giáo viên"],
    cta: "Bắt đầu miễn phí",
    isFree: true,
  },
  {
    id: "tutor",
    name: "Gia sư",
    monthly: "50.000đ",
    yearly: "500.000đ/năm",
    tagline: "Cho gia sư, giáo viên dạy kèm",
    features: [
      "1 lớp",
      "10 học viên",
      "Điểm danh + Lịch dạy",
      "Giao bài tập",
      "Thông báo cơ bản",
      "Admin = Giáo viên",
      "Hỗ trợ qua email",
    ],
    cta: "Chọn gói Gia sư",
  },
  {
    id: "center",
    name: "Trung tâm",
    monthly: "199.000đ",
    yearly: "2.000.000đ/năm",
    tagline: "Cho trung tâm vừa và nhỏ",
    features: [
      "10 lớp",
      "100 học viên",
      "Nhiều giáo viên + phân quyền",
      "Dashboard + Báo cáo",
      "Quản lý học phí + lương GV",
      "Thông báo hàng loạt + hẹn giờ",
      "Xuất Excel",
      "Hỗ trợ ưu tiên qua Zalo/chat",
    ],
    cta: "Chọn gói Trung tâm",
    highlight: true,
    badge: "Phổ biến nhất",
  },
  {
    id: "school",
    name: "Trường học",
    monthly: "299.000đ",
    yearly: "3.000.000đ/năm",
    tagline: "Cho trường học, hệ thống nhiều cơ sở",
    features: [
      "Không giới hạn lớp + HS + GV",
      "Tất cả tính năng Trung tâm",
      "Báo cáo nâng cao",
      "Xuất PDF chuyên nghiệp",
      "Quản lý nhiều cơ sở",
      "Hỗ trợ triển khai + đào tạo",
      "SLA cam kết uptime 99.9%",
    ],
    cta: "Chọn gói Trường học",
  },
];

type Cycle = "monthly" | "yearly";
const EASE = [0.32, 0.72, 0, 1] as const;

function DemoButton() {
  const { demoSignIn } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  return (
    <button
      disabled={loading}
      onClick={async () => {
        setLoading(true);
        try {
          await demoSignIn("admin");
          navigate({ to: "/admin" });
        } catch {
          toast.error("Không thể tải demo");
        } finally {
          setLoading(false);
        }
      }}
      className="group inline-flex items-center gap-2 h-11 pl-5 pr-1.5 rounded-full bg-slate-900 text-white text-[14px] font-medium hover:bg-slate-800 transition-all duration-500 disabled:opacity-70 shrink-0"
    >
      {loading ? <Loader2 className="size-4 animate-spin" strokeWidth={1.5} /> : null}
      Xem demo ngay
      <span className="size-8 rounded-full bg-white/15 grid place-items-center group-hover:translate-x-0.5 transition-transform duration-500">
        <ArrowRight className="size-4" strokeWidth={1.5} />
      </span>
    </button>
  );
}

function PricingCard({ plan, cycle }: { plan: Plan; cycle: Cycle }) {
  const ctaBase =
    "mt-6 inline-flex items-center justify-center gap-1.5 h-11 px-4 rounded-full text-sm font-medium transition-all duration-500";

  return (
    <div
      className={cn(
        "relative p-1.5 rounded-[1.75rem] h-full",
        plan.highlight
          ? "bg-gradient-to-br from-blue-500/15 to-indigo-500/10 ring-2 ring-blue-500"
          : "bg-black/[0.02] dark:bg-white/5 ring-1 ring-black/[0.04] dark:ring-white/10",
      )}
    >
      {plan.badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-slate-900 text-white text-[11px] font-medium tracking-wide shadow-[0_4px_16px_rgb(0,0,0,0.12)]">
          {plan.badge}
        </div>
      )}
      <div
        className={cn(
          "rounded-[calc(1.75rem-6px)] bg-white dark:bg-slate-900 p-6 flex flex-col h-full",
          plan.highlight && "shadow-[0_16px_50px_rgb(37,99,235,0.12)]",
        )}
      >
        <div className="font-display text-lg font-bold text-slate-900 dark:text-white">{plan.name}</div>
        <div className="mt-1 text-[13px] text-slate-500 dark:text-slate-400 min-h-[36px] leading-snug">{plan.tagline}</div>

        {plan.isFree ? (
          <div className="mt-5">
            <div className="font-display text-4xl font-bold text-slate-900 dark:text-white">0đ</div>
            <div className="text-[12px] text-slate-500 dark:text-slate-400 mt-1">Mãi mãi miễn phí</div>
          </div>
        ) : (
          <div className="mt-5">
            <div className="flex items-baseline gap-1.5">
              <span className="font-display text-4xl font-bold text-slate-900 dark:text-white">
                {cycle === "yearly" ? plan.yearly.replace("/năm", "") : plan.monthly}
              </span>
              <span className="text-[13px] text-slate-500 dark:text-slate-400">/{cycle === "yearly" ? "năm" : "tháng"}</span>
            </div>
            <div className="text-[12px] text-slate-400 dark:text-slate-500 mt-1">
              {cycle === "yearly" ? `≈ ${plan.monthly}/tháng` : `hoặc ${plan.yearly}`}
            </div>
          </div>
        )}

        <ul className="mt-6 space-y-2.5 flex-1">
          {plan.features.map((f) => (
            <li key={f} className="flex items-start gap-2.5 text-[13.5px] text-slate-600 dark:text-slate-400 leading-relaxed">
              <Check
                className={cn(
                  "mt-0.5 size-4 shrink-0",
                  plan.highlight ? "text-blue-600" : "text-emerald-600",
                )}
                strokeWidth={2}
              />
              {f}
            </li>
          ))}
        </ul>

        {plan.isFree ? (
          <Link to="/signup" className={cn(ctaBase, "bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white hover:bg-slate-200")}>
            {plan.cta}
          </Link>
        ) : (
          <Link
            to="/signup"
            search={{ plan: plan.id, cycle } as any}
            className={cn(
              ctaBase,
              plan.highlight
                ? "bg-slate-900 text-white hover:bg-slate-800"
                : "bg-white dark:bg-slate-900 text-slate-900 dark:text-white ring-1 ring-slate-200 dark:ring-slate-800 hover:ring-slate-300 hover:bg-slate-50 dark:bg-slate-950",
            )}
          >
            {plan.cta}
            <ArrowRight className="size-4" strokeWidth={1.5} />
          </Link>
        )}
      </div>
    </div>
  );
}

export function LandingPricing() {
  const [cycle, setCycle] = React.useState<Cycle>("monthly");

  return (
    <section id="pricing" className="py-24 md:py-32 px-4 bg-slate-50 dark:bg-slate-950/50">
      <div className="max-w-6xl mx-auto">
        {/* Demo highlight — double bezel */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: EASE }}
          className="mb-12 p-1.5 rounded-[1.5rem] bg-gradient-to-br from-blue-500/10 to-indigo-500/5 ring-1 ring-blue-200/40"
        >
          <div className="rounded-[calc(1.5rem-6px)] bg-white dark:bg-slate-900 p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="size-10 rounded-xl bg-slate-900 text-white grid place-items-center shrink-0">
                <Monitor className="size-5" strokeWidth={1.5} />
              </div>
              <div>
                <div className="font-display text-base font-bold text-slate-900 dark:text-white">
                  Xem demo trước khi quyết định
                </div>
                <p className="text-[13px] text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">
                  Trải nghiệm đầy đủ với dữ liệu mẫu 1000+ học viên. Không cần đăng ký.
                </p>
              </div>
            </div>
            <DemoButton />
          </div>
        </motion.div>

        {/* Heading + cycle toggle */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 mb-10">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: EASE }}
              className="inline-flex px-3.5 py-1.5 rounded-full bg-violet-50 dark:bg-violet-500/10 ring-1 ring-violet-100 text-[11px] uppercase tracking-[0.15em] font-medium text-violet-700 dark:text-violet-300 mb-4"
            >
              Bảng giá
            </motion.div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">
              Giá đơn giản, minh bạch
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mt-3 max-w-md">
              Nâng cấp hoặc hạ cấp bất kỳ lúc nào. Không hợp đồng ràng buộc.
            </p>
          </div>

          <div className="flex items-center gap-1 p-1 rounded-full bg-white dark:bg-slate-900 ring-1 ring-slate-200 dark:ring-slate-800 shadow-[0_8px_30px_rgb(0,0,0,0.04)] shrink-0">
            <button
              type="button"
              onClick={() => setCycle("monthly")}
              className={cn(
                "relative px-4 py-2 rounded-full text-[13px] font-medium transition-colors duration-300",
                cycle === "monthly" ? "text-white" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200",
              )}
            >
              {cycle === "monthly" && (
                <motion.span
                  layoutId="pricing-toggle"
                  className="absolute inset-0 rounded-full bg-slate-900 dark:bg-slate-700 shadow-sm"
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
              )}
              <span className="relative z-10">Theo tháng</span>
            </button>
            <button
              type="button"
              onClick={() => setCycle("yearly")}
              className={cn(
                "relative px-4 py-2 rounded-full text-[13px] font-medium transition-colors duration-300 inline-flex items-center gap-1.5",
                cycle === "yearly" ? "text-white" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200",
              )}
            >
              {cycle === "yearly" && (
                <motion.span
                  layoutId="pricing-toggle"
                  className="absolute inset-0 rounded-full bg-slate-900 dark:bg-slate-700 shadow-sm"
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
              )}
              <span className="relative z-10">Theo năm</span>
              <span
                className={cn(
                  "relative z-10 text-[10px] px-1.5 py-0.5 rounded-full font-semibold",
                  cycle === "yearly" ? "bg-emerald-400 text-emerald-950" : "bg-emerald-100 text-emerald-700 dark:text-emerald-300",
                )}
              >
                −17%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing cards — 1 row */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
          {PLANS.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.08, duration: 0.6, ease: EASE }}
              className="h-full"
            >
              <PricingCard plan={p} cycle={cycle} />
            </motion.div>
          ))}
        </div>

        <p className="mt-10 text-center text-[12.5px] text-slate-400 dark:text-slate-500">
          Giá đã bao gồm cập nhật và hỗ trợ trong suốt thời hạn sử dụng.
        </p>
      </div>
    </section>
  );
}
